import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { usePriceOverview, usePriceHistory } from '../hooks/usePurchases';
import { useItems } from '../../inventory/hooks/useInventory';

// ── helpers ────────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });

function TrendIcon({ pct }: { pct: number | null }) {
  if (pct === null) return <Minus size={14} className="text-muted" />;
  if (pct > 0) return <TrendingUp size={14} className="text-red-500" />;
  if (pct < 0) return <TrendingDown size={14} className="text-emerald-500" />;
  return <Minus size={14} className="text-muted" />;
}

function MiniChart({ history }: { history: { unit_price: number }[] }) {
  if (history.length < 2) return null;
  const prices = history.map(h => h.unit_price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const w = 120;
  const h = 36;
  const pts = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * w;
    const y = h - ((p - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  const last = prices[prices.length - 1];
  const first = prices[0];
  const rising = last > first;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <polyline
        points={pts}
        fill="none"
        stroke={rising ? '#ef4444' : '#10b981'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ItemPriceRow({ item }: { item: any }) {
  const [open, setOpen] = useState(false);
  const { data: history } = usePriceHistory(open ? item.inventory_item_id : undefined);
  const change = item.stats.total_change;

  return (
    <div className="rounded-2xl border border-divider overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 hover:bg-surface-muted transition text-left"
      >
        {/* Mini chart */}
        <div className="hidden sm:block shrink-0">
          <MiniChart history={item.last_entries ?? []} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[color:var(--color-text)]">{item.item_name}</p>
          <p className="text-xs text-muted">{item.purchase_count} compras · última: {fmtDate(item.last_purchase_at)}</p>
        </div>

        {/* Stats */}
        <div className="shrink-0 text-right space-y-0.5">
          <p className="text-base font-bold text-[color:var(--color-text)]">
            {fmt(item.stats.last)}<span className="text-xs font-normal text-muted">/{item.item_unit}</span>
          </p>
          <div className="flex items-center gap-1 justify-end">
            <TrendIcon pct={change} />
            <span className={`text-xs font-semibold ${change > 0 ? 'text-red-600' : change < 0 ? 'text-emerald-600' : 'text-muted'}`}>
              {change > 0 ? '+' : ''}{change ?? 0}% total
            </span>
          </div>
        </div>

        {/* Expand */}
        <div className="ml-2 shrink-0 text-muted">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded: full price history */}
      {open && (
        <div className="border-t border-divider bg-surface-muted px-5 py-4">
          {!history ? (
            <div className="h-24 animate-pulse rounded-xl bg-surface" />
          ) : history.history.length === 0 ? (
            <p className="text-sm text-muted text-center py-4">Sin historial de precios</p>
          ) : (
            <div className="space-y-1">
              {/* Stats bar */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Mínimo', val: fmt(history.stats.min), color: 'text-emerald-600' },
                  { label: 'Máximo', val: fmt(history.stats.max), color: 'text-red-600' },
                  { label: 'Promedio', val: fmt(history.stats.avg), color: 'text-primary' },
                  { label: 'Último', val: fmt(history.stats.last), color: 'text-[color:var(--color-text)]' },
                ].map(s => (
                  <div key={s.label} className="rounded-xl bg-surface p-3 text-center border border-divider">
                    <p className="text-[10px] text-muted uppercase tracking-wide">{s.label}</p>
                    <p className={`text-sm font-bold ${s.color}`}>{s.val}</p>
                  </div>
                ))}
              </div>

              {/* History rows */}
              <div className="text-[10px] font-bold uppercase text-muted grid grid-cols-12 gap-2 px-1 mb-1">
                <div className="col-span-3">Fecha</div>
                <div className="col-span-3">Proveedor</div>
                <div className="col-span-2">Cantidad</div>
                <div className="col-span-2">Precio/unidad</div>
                <div className="col-span-2 text-right">Variación</div>
              </div>
              {[...history.history].reverse().map((entry, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-xl px-1 py-1.5 hover:bg-surface transition">
                  <div className="col-span-3 text-xs text-muted">{fmtDate(entry.date)}</div>
                  <div className="col-span-3 text-xs font-medium text-[color:var(--color-text)] truncate">{entry.supplier}</div>
                  <div className="col-span-2 text-xs text-muted">{entry.quantity} {entry.unit}</div>
                  <div className="col-span-2 text-xs font-bold text-[color:var(--color-text)]">{fmt(entry.unit_price)}</div>
                  <div className="col-span-2 flex justify-end items-center gap-1">
                    <TrendIcon pct={entry.price_change} />
                    <span className={`text-xs font-semibold ${entry.price_change && entry.price_change > 0 ? 'text-red-500' : entry.price_change && entry.price_change < 0 ? 'text-emerald-500' : 'text-muted'}`}>
                      {entry.price_change !== null
                        ? `${entry.price_change > 0 ? '+' : ''}${entry.price_change}%`
                        : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
const PriceHistoryPage: React.FC = () => {
  const { data: overview, isLoading } = usePriceOverview();
  const [q, setQ] = useState('');
  const overviewList = Array.isArray(overview) ? overview : [];

  const filtered = overviewList.filter(i =>
    i.item_name.toLowerCase().includes(q.toLowerCase())
  );

  const mostExpensive = [...overviewList].sort((a, b) => b.stats.last - a.stats.last).slice(0, 3);
  const mostRising    = [...overviewList].sort((a, b) => b.stats.total_change - a.stats.total_change).slice(0, 3);

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted">Compras</p>
          <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-2">
            <TrendingUp size={28} className="text-primary" /> Historial de Precios
          </h1>
          <p className="mt-1 text-sm text-muted">Evolución histórica del precio de tus insumos</p>
        </div>

        {/* Alert cards */}
        {mostRising.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-3">
            {mostRising.filter(i => i.stats.total_change > 0).map(item => (
              <div key={item.inventory_item_id} className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={14} className="text-red-600" />
                  <span className="text-xs font-bold text-red-800 uppercase">Alza de precio</span>
                </div>
                <p className="font-bold text-sm text-red-900">{item.item_name}</p>
                <p className="text-xs text-red-700">+{item.stats.total_change}% total · hoy {fmt(item.stats.last)}/{item.item_unit}</p>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Buscar insumo..."
            value={q}
            onChange={e => setQ(e.target.value)}
            className="w-full rounded-full border border-divider bg-surface pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Items list */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-surface-muted" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted">
            <TrendingUp size={48} className="mx-auto opacity-20 mb-3" />
            <p className="text-sm">Sin datos de precios aún. Registra facturas de compra para ver el historial.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => (
              <ItemPriceRow key={item.inventory_item_id} item={item} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default PriceHistoryPage;
